// pipe (Director tasks): the desktop watcher for task deliveries. Headless and mounted once in the
// sidebar, it polls the caller's open deliveries and own inbox on the shared ERP refresh tick,
// shows each NEW delivery (keyed on `id:version`) once as a toast carrying the same card and
// actions as My tasks, and publishes the sidebar badge count (`useDirectorCounts`). A first poll
// after page load stays quiet about anything older than a minute, more than three new items become
// one summary toast, nothing is toasted while My tasks is open (its cards are in front of the
// user), and a failing poll is silent. A task delivery's toast stays until it is handled; the daily
// prompt and brief close themselves (their cards stay on My tasks and in the badge until cleared).
// A message's toast shows what was written with a quick-reply box: the reply is an ordinary comment
// posted without leaving the screen, which also marks the message seen; Escape closes the toast and
// keeps the draft for the message's card on My tasks.
// The mobile edition has no watcher: its deliveries are the cards at the top of My tasks, and its
// tab bar runs the same counts without toasts (`DirectorMobileBadgeSource`).
import { notify } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { planToasts } from '../../desktop/pages/workspace/director/model/delivery-seen';
import {
  directorHref,
  type DirectorTarget,
} from '../../desktop/pages/workspace/director/model/url';
import { useDirectorCounts } from '../../desktop/pages/workspace/director/model/use-director-counts';
import {
  useTaskWrites,
  type WriteResult,
} from '../../desktop/pages/workspace/director/model/use-task-writes';
import { QUICK_REPLY_CLOSE_MS } from '../../desktop/pages/workspace/director/ui/quick-reply';
import {
  CONFIRMATION_MS,
  type DeliveryAction,
  DeliveryCard,
  deliveryTask,
  type ItemStatus,
} from '../../desktop/pages/workspace/director/views/delivery-card';
import type { DirectorDelivery } from '../../modules/erp/director-tasks-types';
import { WorkbenchService } from '../../modules/workbench';

/** A summary toast is information only; a task delivery's toast stays until it is acted on or
 *  cleared. */
const SUMMARY_TOAST_MS = 15_000;
/** The end-of-day prompt and the evening brief close themselves after this long. */
export const DAILY_TOAST_MS = 20_000;

const toastDuration = (delivery: DirectorDelivery) =>
  deliveryTask(delivery) ? Number.POSITIVE_INFINITY : DAILY_TOAST_MS;

// --- One delivery toast -------------------------------------------------------------------------
interface ToastHandlers {
  accept: (delivery: DirectorDelivery) => Promise<WriteResult>;
  ask: (delivery: DirectorDelivery, question: string) => Promise<WriteResult>;
  reply: (delivery: DirectorDelivery, text: string) => Promise<WriteResult>;
  open: (delivery: DirectorDelivery) => void;
  dismiss: (delivery: DirectorDelivery) => void;
}

function failureStatus(result: WriteResult): ItemStatus {
  if (result.ok) return {};
  return result.kind === 'conflict'
    ? { notice: { kind: 'conflict' } }
    : { notice: { kind: 'error', message: result.message } };
}

function DeliveryToast({
  delivery,
  handlers,
  onClose,
}: {
  delivery: DirectorDelivery;
  handlers: ToastHandlers;
  onClose: () => void;
}) {
  const [pending, setPending] = useState(false);
  const [status, setStatus] = useState<ItemStatus>({});
  const [draft, setDraft] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const closeSoon = useCallback(
    (after: number = CONFIRMATION_MS) => {
      timer.current = setTimeout(onClose, after);
    },
    [onClose]
  );

  const write = useCallback(
    (send: () => Promise<WriteResult>, done: ItemStatus) => {
      if (pending) return;
      setPending(true);
      send()
        .then(result => {
          if (!result.ok) return setStatus(failureStatus(result));
          setDraft(null);
          setStatus(done);
          closeSoon();
        })
        .catch(() => {}) // useTaskWrites resolves every failure into a WriteResult
        .finally(() => setPending(false));
    },
    [closeSoon, pending]
  );

  const onAction = (target: DirectorDelivery, action: DeliveryAction) => {
    switch (action) {
      case 'accept':
        return write(() => handlers.accept(target), { accepted: true });
      case 'ask':
        return setDraft('');
      case 'open':
        handlers.open(target);
        return onClose();
      case 'dismiss':
        handlers.dismiss(target);
        return onClose();
    }
  };

  const reply = useMemo(
    () => ({
      send: (text: string) => handlers.reply(delivery, text),
      onSent: () => closeSoon(QUICK_REPLY_CLOSE_MS),
      onEscape: onClose,
    }),
    [closeSoon, delivery, handlers, onClose]
  );

  const question = draft?.trim() ?? '';
  return (
    <DeliveryCard
      delivery={delivery}
      now={new Date()}
      pending={pending}
      status={status}
      reply={delivery.kind === 'message' ? reply : null}
      ask={
        draft === null
          ? null
          : {
              draft,
              onChange: setDraft,
              onSend: () => {
                if (question)
                  write(() => handlers.ask(delivery, question), {
                    asked: true,
                  });
              },
              onCancel: () => setDraft(null),
            }
      }
      onAction={onAction}
    />
  );
}

// --- The watcher --------------------------------------------------------------------------------
interface OpenToast {
  toastId: string | number;
  version: number;
  /** A write started from the toast: it stays to show the outcome even once the item is gone. */
  hold: boolean;
}

const MY_TASKS: DirectorTarget = { kind: 'view', view: 'tasks' };
const OWN_FILTERS = { scope: 'mine', companyId: null } as const;

export const DirectorDeliveryWatcher = () => {
  const t = useI18n();
  const workbench = useService(WorkbenchService).workbench;
  const onMyTasks$ = useMemo(
    () =>
      workbench.location$.selector(
        l =>
          l.pathname === '/director' &&
          new URLSearchParams(l.search).get('view') === 'tasks'
      ),
    [workbench]
  );
  const onMyTasks = useLiveData(onMyTasks$);
  const quiet = useRef(onMyTasks);
  quiet.current = onMyTasks;

  // One poll feeds both the toasts and the badge.
  const { deliveries, inbox } = useDirectorCounts();
  const { act, reply, clear } = useTaskWrites();
  const { items, state, reload: reloadDeliveries } = deliveries;
  const { reload: reloadInbox } = inbox;

  const latest = useRef(items);
  latest.current = items;
  const toasts = useRef(new Map<string, OpenToast>());

  const go = useCallback(
    (target: DirectorTarget) =>
      workbench.open(directorHref(target, OWN_FILTERS)),
    [workbench]
  );

  const handlers = useMemo<ToastHandlers>(() => {
    const revalidate = () => {
      reloadDeliveries();
      reloadInbox();
    };
    /** The task as the newest poll has it, so a write never carries a version the toast outlived. */
    const current = (delivery: DirectorDelivery) =>
      latest.current.find(d => d.id === delivery.id) ?? delivery;
    const taskOf = (delivery: DirectorDelivery) =>
      deliveryTask(current(delivery));
    const held = async (
      delivery: DirectorDelivery,
      send: () => Promise<WriteResult>
    ): Promise<WriteResult> => {
      const entry = toasts.current.get(delivery.id);
      if (entry) entry.hold = true;
      const result = await send();
      if (!result.ok && entry) entry.hold = false;
      revalidate();
      return result;
    };
    const notAllowed: WriteResult = {
      ok: false,
      kind: 'forbidden',
      message: '',
    };
    return {
      accept: delivery =>
        held(delivery, () => {
          const task = taskOf(delivery);
          return task
            ? act(task, { action: 'accept' })
            : Promise.resolve(notAllowed);
        }),
      ask: (delivery, question) =>
        held(delivery, () => {
          const task = taskOf(delivery);
          return task
            ? act(task, { action: 'ask', question })
            : Promise.resolve(notAllowed);
        }),
      reply: (delivery, text) =>
        held(delivery, async () => {
          const task = taskOf(delivery);
          if (!task) return notAllowed;
          const result = await reply(task.id, text);
          // A reply is the read receipt. A newer message that arrived meanwhile keeps the delivery
          // open (its version moved on), so it is still shown.
          if (result.ok) await clear(current(delivery), 'reply');
          return result;
        }),
      open: delivery => {
        clear(delivery, 'open').then(revalidate, () => {});
        const task = deliveryTask(delivery);
        go(
          task
            ? { kind: 'task', taskId: task.id }
            : {
                kind: 'view',
                view: delivery.kind === 'eodPrompt' ? 'today' : 'inbox',
              }
        );
      },
      dismiss: delivery => {
        clear(delivery, 'gotIt').then(revalidate, () => {});
      },
    };
  }, [act, clear, go, reloadDeliveries, reloadInbox, reply]);

  useEffect(() => {
    if (state !== 'ready') return;
    // A toast whose delivery was cleared elsewhere, or refreshed to a newer version, goes.
    for (const [id, open] of toasts.current) {
      if (open.hold) continue;
      const current = items.find(d => d.id === id);
      if (!current || current.version !== open.version) {
        notify.dismiss(open.toastId);
        toasts.current.delete(id);
      }
    }
    const plan = planToasts(items, { now: Date.now(), quiet: quiet.current });
    for (const delivery of plan.show) {
      const forget = () => {
        if (toasts.current.get(delivery.id)?.version === delivery.version)
          toasts.current.delete(delivery.id);
      };
      const toastId = notify.custom(
        ({ onDismiss }) => (
          <DeliveryToast
            delivery={delivery}
            handlers={handlers}
            onClose={() => {
              forget();
              onDismiss?.();
            }}
          />
        ),
        { duration: toastDuration(delivery), onAutoClose: forget }
      );
      toasts.current.set(delivery.id, {
        toastId,
        version: delivery.version,
        hold: false,
      });
    }
    if (plan.summary > 0)
      notify(
        {
          title: t['com.affine.pipe.director.tasks.delivery.summary']({
            n: String(plan.summary),
          }),
          actions: [
            {
              key: 'open',
              label:
                t['com.affine.pipe.director.tasks.delivery.open-my-tasks'](),
              onClick: () => go(MY_TASKS),
            },
          ],
        },
        { duration: SUMMARY_TOAST_MS }
      );
  }, [go, handlers, items, state, t]);

  return null;
};
